<i18n src='@/locale/prescription-form-localization.json'></i18n>

<template>
    <v-container class="pt-7">
        <v-row
            v-if="formData.id"
            dense
        >
            {{ FormatDate(formData.datetime) }}
        </v-row>
        <v-row
            v-if="!formData.id"
            dense
        >
            <v-col
                dense
                cols="3"
            >
                <v-wm-date-picker-menu
                    v-model="formData.startDate"
                    :label="$t('start_time')"
                    @input="dateChanged()"
                ></v-wm-date-picker-menu>
            </v-col>
            <v-col
                dense
                cols="3"
            >
                <v-text-field
                    v-model="receptionDays"
                    @input="addDaysToDate(receptionDays)"
                    type="number"
                    outlined
                    dense
                    :label="$t('number_of_days')"
                    hide-details
                ></v-text-field>
            </v-col>
            <v-col
                dense
                cols="3"
            >
                <v-wm-date-picker-menu
                    v-model="formData.endDate"
                    :label="$t('end_time')"
                    :min="formData.startDate"
                    @input="dateChanged()"
                ></v-wm-date-picker-menu>
            </v-col>
        </v-row>
        <v-row
            v-if="!formData.id"
            dense
        >
            <v-col
                dense
                style="display: flex;"
            >
                <div
                    class="times"
                    :class="{
                        invalid: !formData.times.length && formData.whenToTake !== 5
                    }"
                >
                    <span 
                        class="circle" 
                        v-for="item in hours"
                        :class="{'active': hourIsActive(numberToTimeFormat(item))}"
                        @click="selectTime(numberToTimeFormat(item))"
                    > {{ item }} </span>
                </div>
                <v-select
                    dense
                    outlined
                    :items="minutes"
                    v-model="formData.startMinute"
                    hide-details
                    class="start-minute-select"
                ></v-select>
                <v-btn
                    v-if="hasRestoreTimeData"
                    color="primary"
                    @click="restoreTimeData"
                    style="margin-left: 10px;"
                >
                    {{ $t('RestoreTime') }}
            </v-col>
        </v-row>
        <v-row dense>
            <v-col
                dense
                cols="3"
            >
                <v-checkbox
                    dense
                    v-model="customMedicament"
                    :label="$t('Prescription.Patient_Medicament')"
                ></v-checkbox>
            </v-col>
            <v-col
                dense
                cols="3"
            >
                <v-checkbox
                    v-if="!customMedicament"
                    dense
                    v-model="formData.allMedicament"
                    :label="$t('All_Medicament')"
                ></v-checkbox>
            </v-col>
            <v-col
                dense
                cols="3"
            >
                <v-wm-department-select
                    v-model="formData.departmentCode"
                    clearable
                    :class="{
                        invalid: !formData.departmentCode
                    }"
                    :has-department-access-with-type="8"
                ></v-wm-department-select>
            </v-col>
            <v-col
                v-if="false"
                dense
                cols="3"
            >
                <v-checkbox
                    dense
                    v-model="formData.withoutUnit"
                    :label="$t('Prescription.WithoutUnit')"
                    :class="{
                        invalid: !formData.withoutUnit && !formData.unitId
                    }"
                ></v-checkbox>
            </v-col>
        </v-row>
        <v-row
            dense
        >
            <v-col
                dense
                style="display: flex;"
                cols="9"
            >
                <v-text-field
                    v-if="customMedicament"
                    dense
                    outlined
                    v-model="formData.customMedicamentName"
                    :label="$t('Medicament')"
                    hide-details
                ></v-text-field>
                <searchMedicamentByMohGenericDialog
                    v-if="!customMedicament && formData.departmentCode"
                    :department-code="formData.departmentCode"
                    @select="medicament.code = $event.Code"
                ></searchMedicamentByMohGenericDialog>
                <v-wm-medicament-select
                    v-if="!customMedicament"
                    :withBrunvaAccess="!formData.allMedicament"
                    :include-deleted="!formData.allMedicament"
                    :department-code="formData.departmentCode"
                    only-ehr-medicaments
                    check-with-pharmacy
                    :patient-personal-number="patientPersonalNumber"
                    v-model="medicament.code"
                    @unit="setMedicamentUnitData(medicament, $event)"
                    :history-code="formData.historyCode"
                    :datetime-for-department-determination="formData.times && formData.times.length ? `${formData.startDate}T${formData.times[0].substring(0, 2)}:${formData.startMinute}:00` : ''"
                ></v-wm-medicament-select>
                <set-unit
                    v-if="!customMedicament && medicament.unitVis"
                    :code="medicament.code"
                    @save="updateMedicamentUnit(medicament, $event.unitId, $event.unitQuantity)"
                    @cancel="medicament.unitVis = false"
                ></set-unit>
                <v-btn
                    v-if="!customMedicament"
                    color="primary"
                    dense
                    @click="openMedicamentUnitForm(medicament)"
                >
                    <span v-if="medicament.unit && medicament.unit.name">
                        {{ medicament.unit.quantity }} {{ medicament.unit.name }}
                    </span>
                    <span v-else>
                        U
                    </span>
                </v-btn>
            </v-col>
            <v-col
                dense
                cols="3"
                style="display: flex;"
            >
                <v-text-field
                    type="number"
                    outlined
                    dense
                    :label="$t('Amount')"
                    hide-details
                    v-model="medicament.quantity"
                    :suffix="customMedicament ? null : unitName"
                    :class="{
                        invalid: !medicament.quantity
                    }"
                ></v-text-field>
                <unit-select
                    v-if="(customMedicament || medicament.unit.type > 0) && !formData.withoutUnit"
                    :unit-type="customMedicament ? null : medicament.unit.type"
                    :medicament-code="customMedicament ? null : medicament.code"
                    v-model="formData.unitId"
                    @unit="setUnit($event)"
                    clearable
                    :class="{
                        invalid: !formData.withoutUnit && !formData.unitId
                    }"
                ></unit-select>
            </v-col>
        </v-row>
        <v-row
            dense
        >
            <v-col
                dense
                cols="2"
            >
                <v-select
                    outlined
                    dense
                    clearable
                    :items="receptionTimes"
                    v-model="formData.whenToTake"
                    hide-details
                ></v-select>
            </v-col>
            <v-col
                dense
                cols="3"
            >
                <v-select
                    outlined
                    dense
                    clearable
                    :items="receptionRules"
                    v-model="formData.howToTake"
                    hide-details
                    :class="{
                        invalid: !formData.howToTake
                    }"
                ></v-select>
            </v-col>
            <v-col
                dense
                cols="3"
                class="d-flex align-center"
            >
                <div
                    v-if="!formData.id"
                >
                    <span class="font-weight-bold">{{ Number(amountPerTake) ? amountPerTake : '' }} {{ unitName }}</span>
                    დღეში <span class="font-weight-bold">{{ this.formData.times.length }}</span>-ჯერ,
                    სულ <span class="indigo--text font-weight-bold">{{ totalQuantity }} {{ unitName }}</span>
                </div>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col
                dense
            >
                <v-textarea
                    dense
                    outlined
                    hide-details
                    v-model="formData.comment"
                    :label="$t('Comment')"
                ></v-textarea>
            </v-col>
        </v-row>
        <v-row dense v-if="!hideActions">
            <v-col
                dense
                style="margin-left: auto; max-width: fit-content;"
            >
                <v-btn
                    v-if="formData.id"
                    color="success"
                    @click="saveAllAfter"
                    :disabled="!isValid"
                    style="margin-right: 5px;"
                    depressed
                >
                    <v-icon left>save</v-icon>
                    {{ $t('Prescription.SaveAllAfter') }}
                </v-btn>
                <v-btn
                    color="success"
                    @click="save(true)"
                    :disabled="!isValid"
                    style="margin-right: 5px;"
                    depressed
                >
                    <v-icon left>save</v-icon>
                    {{ $t('Save') }}
                </v-btn>
                <v-btn
                    v-if="!formData.id"
                    color="success"
                    @click="save(false)"
                    :disabled="!isValid"
                    style="margin-right: 5px;"
                    depressed
                >
                    <v-icon left>save</v-icon>
                    {{ $t('SaveAndContinue') }}
                </v-btn>
                <v-btn
                    color="error"
                    @click="cancel"
                    depressed
                >
                    {{ $t('Cancel') }}
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import VWmDatePickerMenu from '@/component/date-picker-menu.vue';
import VWmDepartmentSelect from '@/component/department-select.vue';
import VWmMedicamentSelect from '@/component/medicament-select.vue';
import receptionTimes from '@/script/prescription-form/reception-times.js'
import receptionRules from '@/script/prescription-form/reception-rules.js'
import prescriptionService from '@/service/prescription-service';
import SetUnit from '../medicament/set-unit.vue';
import unitSelect from '../unit-select.vue';
import unitService from '@/service/unit-service';
import { getDateTimeStr } from '@/script/common';
import searchMedicamentByMohGenericDialog from '@/component/medicament/search-by-moh-generic-dialog.vue';

export default {
    components: {
        VWmDatePickerMenu,
        VWmDepartmentSelect,
        VWmMedicamentSelect,
        SetUnit,
        unitSelect,
        searchMedicamentByMohGenericDialog
    },
    props: {
        formDataProp: {
            type: Object,
            default() {
                return {};
            }
        },
        hideActions: {
            type: Boolean,
            default: false
        },
        patientPersonalNumber: String,
        simpleOrderInAmbulatory: Boolean,
        historyFrom: String
    },
    data() {
        return {
            receptionTimes: receptionTimes,
            receptionRules: receptionRules,
            receptionDays: null,
            formData: {
                duplicate: null,
                id: null,
                historyCode: null,
                diagnosisNumber: null,
                
                startDate: null,
                startMinute: null,
                endDate: null,
                allMedicament: null,
                departmentCode: null,
                whenToTake: null,
                howToTake: null,
                unitId: null,
                times: [],
                comment: null,
                withoutUnit: false
            },
            unit: null,
            customMedicament: false,
            medicament: {
                code: null,
                quantity: null,
                unitVis: false,
                unit: {
                    id: null,
                    name: null,
                    type: null,
                    rate: null,
                    quantity: null
                }
            },
            minutes: Array.from({ length: 60 }, (_, index) => index.toString().padStart(2, '0')),
            timeDataToRestore: {}
        };
    },
    computed: {
        hours() {
            const hours = Array.from({ length: 24 }, (_, index) => index < 10 ? '0' + index : index);
            return hours;
        },
        amountPerTake() {
            return +this.medicament.quantity || 0;
        },
        totalQuantity() {
            return this.round(
                Number(this.amountPerTake)
                    ? this.formData.times.length * Number(this.receptionDays) * Number(this.amountPerTake)
                    : 0
            );
        },
        isValid() {
            return this.formData.startDate
                && this.formData.endDate
                && this.formData.howToTake
                && this.medicament.quantity
                && this.formData.departmentCode
                && (this.customMedicament || this.medicament.code)
                && (this.formData.withoutUnit || (this.unit && this.unit.id))
                && (this.formData.times.length || this.formData.whenToTake === 5);
        },
        unitName() {
            return this.unit && this.unit.id ? this.unit.name : '';
        },
        datetimes() {
            let list = [];
            let dayInMilliseconds = 24 * 60 * 60 * 1000;
            let endDay = Math.floor(new Date(this.formData.endDate).getTime() / dayInMilliseconds);
            for (let day = Math.floor(new Date(this.formData.startDate).getTime() / dayInMilliseconds); day <= endDay; day++) {
                for (let i = 0; i < this.formData.times.length; i++) {
                    const time = this.formData.times[i];
                    let datetime = new Date(day * dayInMilliseconds);
                    datetime.setHours(datetime.getHours() + +time.substring(0, 2));
                    list.push(datetime);
                }
            }
            return list;
        },
        userId() {
            return this.$store.state.PersonCode;
        },
        hasRestoreTimeData() {
            return this.timeDataToRestore
                && Object.keys(this.timeDataToRestore).length
                && Object.keys(this.timeDataToRestore).some(key => this.timeDataToRestore[key]);
        }
    },
    watch: {
        formDataProp: {
            deep: true,
            handler(val) {
                this.formData = { ...val };
                if (!this.formData.times) {
                    this.formData.times = [];
                }

                let now = new Date();
                now.setHours(now.getHours() - now.getTimezoneOffset() / 60);
                let date = this.formDataProp.datetime
                    ? this.formDataProp.datetime.substring(0, 10)
                    : now.toISOString().substring(0, 10);
                if (this.simpleOrderInAmbulatory) {
                    date += 'T09:00:00';
                    if (
                        this.historyFrom
                        && new Date(this.historyFrom) > new Date(date)
                    ) {
                        let startMinute = this.formDataProp.datetime ? this.formDataProp.datetime.substring(14, 16) : '00';
                        date = this.historyFrom.substring(0, 10);
                        this.formDataProp.datetime = this.historyFrom;
                        startMinute = this.historyFrom.substring(14, 16);

                        this.formData.startDate = date;
                        this.formData.times.push(this.historyFrom.substring(11, 13) + ':00:00');
                        this.formData.startMinute = startMinute;
                        this.formData.endDate = date;
                    } else if (
                        !this.formDataProp.datetime
                        && !this.formDataProp.datetime != date
                    ) {
                        this.formDataProp.datetime = date;
                        date = date.substring(0, 10);
                        this.formData.startDate = date;
                        this.formData.times.push(date.substring(11, 13) + ':00:00');
                        this.formData.startMinute = date.substring(14, 16);
                        this.formData.endDate = date;
                    }
                } else {
                    this.formData.startDate = date;
                    this.formData.startMinute = 0;
                    this.formData.endDate = date;
                }
            }
        },
        "formData.departmentCode"(val) {
            this.saveDepartmentCode(val);
        },
    },
    methods: {
        FormatDate(datetime) {
            return getDateTimeStr(datetime, { withoutTimezone: true });
        },
        dateChanged() {
            if(this.formData.startDate && this.formData.endDate) {
                this.daysDifference(this.formData.startDate, this.formData.endDate);
            }
        },
        daysDifference(startDate, endDate) {
            const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
            const startTimestamp = new Date(startDate).getTime();
            const endTimestamp = new Date(endDate).getTime();
            const differenceInMilliseconds = endTimestamp - startTimestamp;
            const differenceInDays = Math.floor(differenceInMilliseconds / oneDayInMilliseconds);
            
            this.receptionDays = differenceInDays + 1;
        },
        addDaysToDate(days) {
            if (this.formData.startDate) {
                const newDate = new Date(this.formData.startDate);
                newDate.setDate(newDate.getDate() + (Number(days) - 1));
                this.formData.endDate = newDate.toISOString().substring(0, 10)
            }
        },
        hourIsActive(item) {
            return this.formData.times.includes(item);
        },
        numberToTimeFormat(hours) {
            const timeString = `${String(hours).padStart(2, '0')}:00:00`;
            return timeString;
        },
        selectTime(item) {
            if(this.hourIsActive(item)) {
                const index = this.formData.times.indexOf(item);
                this.formData.times.splice(index, 1);
            } else {
                this.formData.times.push(item);
            }
        },
        openMedicamentUnitForm(medicament) {
            medicament.unitVis = true;
        },
        updateMedicamentUnit(medicament, unitId, unitQuantity) {
            unitService.data.byId(unitId)
                .then(response => {
                    const unit = response;
                    if (unit) {
                        this.setMedicamentUnitData(medicament, {
                            id: unit.id,
                            name: unit.name,
                            type: unit.unitType,
                            rate: unit.rate,
                            quantity: unitQuantity
                        });
                    }
                });
            medicament.unitVis = false;
        },
        setMedicamentUnitData(medicament, unit) {
            if (unit) {
                medicament.unit.id = unit.id;
                medicament.unit.name = unit.name;
                medicament.unit.type = unit.type;
                medicament.unit.rate = unit.rate;
                medicament.unit.quantity = unit.quantity;
            } else {
                medicament.unit.id = null;
                medicament.unit.name = null;
                medicament.unit.type = null;
                medicament.unit.rate = null;
                medicament.unit.quantity = null;
            }
        },
        setMedicamentData(item) {
            this.medicament.code = item.medicamentCode;
            this.medicament.quantity = item.quantity;

            if (item.medicamentUnitId) {
                this.medicament.unit.id = item.medicamentUnitId;
                this.medicament.unit.name = item.medicamentUnitName;
                this.medicament.unit.type = item.medicamentUnitType;
                this.medicament.unit.rate = item.medicamentUnitRate;
                this.medicament.unit.quantity = item.medicamentUnitQuantity;
            }
        },
        setUnit(unit) {
            this.unit = unit;
        },
        round(value) {
            return Math.round(value * 1000) / 1000;
        },
        saveAllAfter() {
            if (!this.isValid || !this.formData.id) {
                return;
            }
            let data = {
                ...this.formData,
                unitId: this.unit ? this.unit.id : null,
                customMedicamentName: this.customMedicament ? this.formData.customMedicamentName : null,
                medicamentCode: this.customMedicament ? null : this.medicament.code,
                quantity: +this.medicament.quantity,
                unitId: this.formData.withoutUnit ? null : this.formData.unitId
            };
            prescriptionService
                .updateAllAfterPrescription(data)
                .then(() => this.$emit('save'));
        },
        async save(closeAfter) {
            if (!this.isValid) {
                return;
            }
            if (this.formData.id) {
                let data = {
                    ...this.formData,
                    unitId: this.unit ? this.unit.id : null,
                    customMedicamentName: this.customMedicament ? this.formData.customMedicamentName : null,
                    medicamentCode: this.customMedicament ? null : this.medicament.code,
                    quantity: +this.medicament.quantity,
                    unitId: this.formData.withoutUnit ? null : this.formData.unitId
                };
                await prescriptionService.updatePrescription(data)
                this.$emit('save', { resetForm: !closeAfter });
                return;
            }
            let data = {
                ...this.formData,
                unitId: this.unit ? this.unit.id : null,
                customMedicamentName: this.customMedicament ? this.formData.customMedicamentName : null,
                medicamentCode: this.customMedicament ? null : this.medicament.code,
                quantity: +this.medicament.quantity,
                unitId: this.formData.withoutUnit ? null : this.formData.unitId
            };
            let list = [];
            if (this.datetimes.length) {
                list = this.datetimes
                    .map(datetime => {
                        datetime.setMinutes(this.formData.startMinute || 0);
                        return {
                            ...data,
                            datetime,
                        };
                    });
            } else if (this.formData.whenToTake === 5) {
                list.push({ ...data });
            }
            if (list.length) {
                let warning = await prescriptionService.validateMaxQuantityPrescription(list);
                if (warning && warning.maxQuantity > 0) {
                    let message = `<div>დოზის გადაჭარბება. მაქსიმალური რაოდენობა: ${warning.maxQuantity}, გამოყენებული რაოდენობა ბოლო 24 საათში: ${warning.usedQuantityForDay} ${warning.description ? `<br>${warning.description}` : ''}</div>`;
                    const confirmed = await this.$confirm(message, { title: this.$t('Warning'), html: true, color: 'warning' });
                    if (!confirmed) return;
                }
                await prescriptionService.createRangePrescription(list)
                this.$emit('save', {
                    resetForm: !closeAfter,
                    startDate: this.formData.startDate,
                    startMinute: this.formData.startMinute,
                    endDate: this.formData.endDate,
                    times: this.formData.times
                });
            }
        },
        cancel() {
            this.$emit('cancel');
        },
        saveDepartmentCode(prescriptionDepartmentCode) {
            if (this.userId) {
                let users = localStorage.users ? JSON.parse(localStorage.users) : {};
                if (!users[this.userId]) users[this.userId] = {};
                users[this.userId]["prescriptionDepartmentCode"] = prescriptionDepartmentCode;
                localStorage.users = JSON.stringify(users);
            }
        },
        loadSavedDepartmentCode(userId) {
            if (localStorage.users) { // if users is in localStorage
                let users = JSON.parse(localStorage.users)
                if (users[userId] && users[userId].prescriptionDepartmentCode) // if user's has prescriptionDepartmentCode
                {
                    this.formData.departmentCode = users[userId].prescriptionDepartmentCode;
                }
            }
        },
        restoreTimeData() {
            this.formData.times = [];
            this.timeDataToRestore.times.forEach(time => this.formData.times.push(time));
            this.formData.startDate = this.timeDataToRestore.startDate;
            this.formData.startMinute = this.timeDataToRestore.startMinute;
            this.formData.endDate = this.timeDataToRestore.endDate;
        }
    },
    async mounted() {
        if (
            this.formDataProp
            && Object.keys(this.formDataProp).length
        ) {
            let startMinute = '00';
            let now = new Date();
            now.setHours(now.getHours() - now.getTimezoneOffset() / 60);
            let date = this.formDataProp.datetime
                ? this.formDataProp.datetime.substring(0, 10)
                : now.toISOString().substring(0, 10);
            if (this.simpleOrderInAmbulatory) {
                startMinute = this.formDataProp.datetime ? this.formDataProp.datetime.substring(14, 16) : '00';
                if (
                    this.historyFrom
                    && new Date(this.historyFrom) > (this.formDataProp.datetime ? new Date(this.formDataProp.datetime) : now.toISOString())
                ) {
                    date = this.historyFrom.substring(0, 10);
                    this.formDataProp.datetime = this.historyFrom;
                    startMinute = this.historyFrom.substring(14, 16);
                } else {
                    this.formDataProp.datetime = date + 'T09:00:00';
                }
            }
            
            let times = this.formDataProp.datetime
                ? [this.formDataProp.datetime.substring(11, 19)]
                : [];
            this.timeDataToRestore = {
                startDate: this.formDataProp.startDate || date,
                startMinute: this.formDataProp.startMinute,
                endDate: this.formDataProp.endDate || date,
                times: this.formDataProp.times
            };
            this.formData = {
                duplicate: this.formDataProp.duplicate,
                id: this.formDataProp.id,
                historyCode: this.formDataProp.historyCode,
                diagnosisNumber: this.formDataProp.diagnosisNumber,

                datetime: this.formDataProp.datetime,
                startDate: date,
                startMinute: startMinute,
                endDate: date,
                allMedicament: this.formDataProp.allMedicament,
                departmentCode: this.formDataProp.departmentCode,
                customMedicamentName: this.formDataProp.customMedicamentName,
                whenToTake: this.formDataProp.whenToTake,
                howToTake: this.formDataProp.howToTake,
                unitId: this.formDataProp.unitId,
                times: times,
                comment: this.formDataProp.comment,
                status: this.formDataProp.status || 1,
                withoutUnit: false // this.formDataProp.withoutUnit
            };
            if (!this.formDataProp.id) {
                this.loadSavedDepartmentCode(this.userId);
            }
            this.medicament.code = this.formDataProp.medicamentCode;
            this.customMedicament = !!this.formData.customMedicamentName;
            this.medicament.quantity = this.formDataProp.quantity;
            if (this.formDataProp.unitId) {
                this.setUnit(await unitService.data.byId(this.formDataProp.unitId));
            }
            this.setMedicamentData(this.formDataProp);
        }
    }
}
</script>

<style scoped>
.invalid {
    border: 2px solid red;
}

.times {
    width: max-content;
    display: grid;
    grid-template-columns: repeat(12,auto);
    gap: 5px;
}
.times .circle {
    cursor: pointer;
    display: inline-block;
    width: 30px;
    height: 30px;
    line-height: 30px;
    text-align: center;
    background-color: royalblue;
    border-radius: 50%;
    color: white;
}
.times .circle.active {
    background-color: green;
}

.start-minute-select {
    width: 100px;
    max-width: 100px;
    margin-top: auto;
    margin-bottom: auto;
    margin-left: 10px;
}
</style>